import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, NGX_SUB_FORM_HANDLE_VALUE_CHANGES_RATE_STRATEGIES, subformComponentProviders } from 'ngx-sub-form';
import { Subject } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { GetValueFromFeet } from 'src/app/perivis/shared/helpers/units.helper';
import { AdvancedExpansionJoint, MovementState, NotShearedState } from 'src/app/wellbore-inputs/models/packers.model';

export interface ExpansionJointForm {
  isDepthNotAtPacker: boolean;
  measuredDepth: number;
  sealBoreContactDiameter: number;
  isSheared: boolean;
  movementState: MovementState;
  notShearedState: NotShearedState;
  sealBoreBelowPacker: boolean;
}
@Component({
  selector: 'app-expansion-joint',
  templateUrl: './expansion-joint.component.html',
  providers: subformComponentProviders(ExpansionJointComponent),
  standalone: false
})

export class ExpansionJointComponent implements OnChanges {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }

  @Input()
  hasExpansionJoint: boolean | false | undefined;
 
  @Input()
  defaultPbrDepth: number | undefined;

  @Input()
  public userUnits: UserUnitsModel | null | undefined;

  @Input()
  public defaultSealBoreDiameter: number | null | undefined;

  @Input()
  public packerDepth: number;

  public upwardMovementLimitValue: string | null | undefined;
  public minStrokeLength: number;

  private input$ = new Subject<AdvancedExpansionJoint | null | undefined>();
  @Input() set advancedExpansionJoint(advancedExpansionJoint: AdvancedExpansionJoint | undefined) {
    this.input$.next(advancedExpansionJoint);
  }

  @Output() expansionJointUpdate = new EventEmitter<AdvancedExpansionJoint>();

  public form = createForm<AdvancedExpansionJoint, ExpansionJointForm>(this, {
    formType: FormType.SUB,
    input$: this.input$,
    output$: this.expansionJointUpdate,
    formControls: {
      isDepthNotAtPacker: new FormControl(false),
      measuredDepth: new FormControl(null, { updateOn: 'blur' }),
      sealBoreContactDiameter: new FormControl(null, { updateOn: 'blur', validators: Validators.required }),
      isSheared: new FormControl(false, Validators.required),
      movementState: new FormGroup({
        totalStrokeLength: new FormControl(null, { updateOn: 'blur', validators: Validators.required }),
        hasUpwardNoGo: new FormControl(false),
        spaceOut: new FormControl(null, { updateOn: 'blur', validators: [Validators.required] })
      }),
      notShearedState: new FormGroup({
        shearPinRating: new FormControl(null, { updateOn: 'blur', validators: Validators.required })
      }),
      sealBoreBelowPacker: new FormControl(false)
    },
    toFormGroup: (obj: AdvancedExpansionJoint| null): ExpansionJointForm | null => {
      if (!obj) {
        return null;
      }

      this.isShearedChange({checked: obj.isSheared});
      this.pbrAtPackerChange({checked: obj.isDepthNotAtPacker});
      this.sealBoreBelowPackerChecked({checked: obj.sealBoreBelowPacker ?? false});

      return {
        isDepthNotAtPacker: obj.isDepthNotAtPacker,
        measuredDepth: obj.measuredDepth,
        sealBoreContactDiameter: obj.sealBoreContactDiameter,
        isSheared: obj.isSheared,
        movementState: obj.movementState ?? null,
        notShearedState: obj.notShearedState ?? null,
        sealBoreBelowPacker: obj.sealBoreBelowPacker
      }
    },
    fromFormGroup: (formValue: ExpansionJointForm | null): AdvancedExpansionJoint | null => {
      if (!formValue) {
        return null;
      }

      const rawValue = this.form.formGroup.getRawValue();

      if ((formValue.isSheared == true && (!rawValue.movementState || rawValue.movementState.spaceOut == null ||
        rawValue.movementState.totalStrokeLength == null || rawValue.movementState.spaceOut > rawValue.movementState.totalStrokeLength)) ||
        (formValue.isSheared == false && rawValue.notShearedState.shearPinRating == null)) {
        return null;
      }

      if (rawValue.movementState.hasUpwardNoGo == null) {
        rawValue.movementState.hasUpwardNoGo = false;
      }

      return {
        isDepthNotAtPacker: rawValue.isDepthNotAtPacker,
        measuredDepth: rawValue.measuredDepth,
        sealBoreContactDiameter: rawValue.sealBoreContactDiameter,
        isSheared: rawValue.isSheared,
        movementState: rawValue.movementState.spaceOut != null &&  rawValue.movementState.totalStrokeLength != null ? rawValue.movementState : null,
        notShearedState: rawValue.notShearedState.shearPinRating != null ? rawValue.notShearedState : null,
        sealBoreBelowPacker: rawValue.sealBoreBelowPacker
      }
    }, handleEmissionRate: NGX_SUB_FORM_HANDLE_VALUE_CHANGES_RATE_STRATEGIES.debounce(250)
  })

  ngOnChanges(): void {
    if (!this.hasExpansionJoint) {
      this.hideExpansionJoint();
    } else {
      this.form.formGroup.controls.sealBoreContactDiameter.enable({emitEvent: false});
      this.form.formGroup.controls.isSheared.enable({emitEvent: false});
      this.setSealBoreContactDiameterValidators(true);

      if (!this.form.formGroup.controls.sealBoreContactDiameter.value) {
        this.form.formGroup.controls.sealBoreContactDiameter.setValue(this.defaultSealBoreDiameter, {emitEvent: false});
      }

      this.pbrAtPackerChange({checked: this.form.formGroup.controls.isDepthNotAtPacker?.value});
      this.movementLimitChange();
      this.isShearedChange({checked: this.form.formGroup.controls.isSheared?.value});
      this.sealBoreBelowPackerChecked({checked: this.form.formGroup.controls.sealBoreBelowPacker?.value});
    }
  }

  private hideExpansionJoint() {
    this.setSealBoreContactDiameterValidators(false);
    this.form.formGroup.controls.measuredDepth.disable({emitEvent: false});
    this.form.formGroup.controls.sealBoreContactDiameter.disable({emitEvent: false});
    this.form.formGroup.controls.isSheared.disable({emitEvent: false});
    this.form.formGroup.controls.movementState.disable({emitEvent: false});
    this.form.formGroup.controls.notShearedState.disable({emitEvent: false});
  }

  private setSealBoreContactDiameterValidators(isRequired: boolean): void {
    const control = this.form.formGroup.controls.sealBoreContactDiameter;

    if (isRequired) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private setSpaceOutState(): void {
    const movementStateGroup = this.form.formGroup.get('movementState') as FormGroup;
    const spaceOutControl = movementStateGroup.get('spaceOut');

    if (!this.form.formGroup.controls.isSheared.value) {
      spaceOutControl?.disable({ emitEvent: false });
    } else {
      spaceOutControl?.enable({ emitEvent: false });
    }
  }

  public isShearedChange(event: any): void {
    if (event.checked) {
      this.form.formGroup.controls.notShearedState.disable({emitEvent: false});
      this.form.formGroup.controls.movementState.enable({emitEvent: false});
    } else {
      this.form.formGroup.controls.notShearedState.enable({emitEvent: false});
      this.form.formGroup.controls.movementState.disable({emitEvent: false});
    }
    this.setSpaceOutState();
    this.movementLimitChange();
  }

  public pbrAtPackerChange(event: any): void {
    const measuredDepthControl = this.form.formGroup.controls.measuredDepth;

    if (event.checked) {
      if (measuredDepthControl.value == null || measuredDepthControl.value >= this.packerDepth) {
        measuredDepthControl.setValue(this.packerDepth - 1, { emitEvent: false });
      }
    }

    measuredDepthControl.setValidators(event.checked ? [Validators.max(this.packerDepth - 1), Validators.required] : []);
    measuredDepthControl.updateValueAndValidity({ emitEvent: false });
    measuredDepthControl[event.checked ? 'enable' : 'disable']({ emitEvent: false });
  }

  public movementLimitChange(event?: any, value?: string): void {
    const spaceOutValue = value == 'spaceOut' ? event?.value : this.form.formGroup.controls.movementState.value?.spaceOut ?? 0
    const totalStrokeLength = value == 'totalStrokeLength' ? event?.value : this.form.formGroup.controls.movementState.value?.totalStrokeLength ?? 0;

    const movementStateGroup = this.form.formGroup.get('movementState') as FormGroup;
    const spaceOutControl = movementStateGroup.get('spaceOut');
    const totalStrokeLengthControl = movementStateGroup.get('totalStrokeLength');
    spaceOutControl?.setValidators([Validators.max(totalStrokeLength), Validators.min(0), Validators.required]);
    const factor = GetValueFromFeet(0.1, this.userUnits.longLengths);
    this.minStrokeLength = spaceOutValue + factor;
    totalStrokeLengthControl?.setValidators([Validators.min(this.minStrokeLength), Validators.required]);
    spaceOutControl?.updateValueAndValidity({ emitEvent: false });
    totalStrokeLengthControl?.updateValueAndValidity({ emitEvent: false });
    
    this.upwardMovementLimitValue = this.form.formGroup.controls.isSheared.value ?
     'Upward movement limit: ' + (totalStrokeLength - spaceOutValue).toFixed(2) + ' ' + this.userUnits.longLengths : '';
  }

  public sealBoreBelowPackerChecked(event: any): void {
    if (event.checked) {
      if (this.form.formGroup.controls.isDepthNotAtPacker.value == true) {
        this.form.formGroup.controls.isDepthNotAtPacker.setValue(false, {emitEvent: true});
      }
      this.form.formGroup.controls.isDepthNotAtPacker.disable({emitEvent: false});
      this.pbrAtPackerChange({checked: false});
    } else {
      this.form.formGroup.controls.isDepthNotAtPacker.enable({emitEvent: false});
    }
  }
}